import { useStore } from '@nanostores/react';
import { useEffect } from 'react';
import { ProPlansCard } from './Card/ProPlansCard';
import { TeamPlansCard } from './Card/TeamPlansCard';
import { PricingIntervalSelector } from './IntervalSelector';
import { UpdatePlanScreenDialog } from '~/components/dialogs/UpdatePlanScreenDialog/UpdatePlanScreenDialog';
import { Dialog, DialogContent, HiddenDialogTitle } from '~/components/ui/Dialog';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { proPricingPlansStore } from '~/lib/stores/pricing/proPlans';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { userStore } from '~/lib/stores/user';

export type UpgradePlanCardType = 'teams' | 'pro';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  cards: UpgradePlanCardType[];
}

export const UpgradePlanDialogs = ({
  isOpen,
  onClose,
  message,
  title = 'Keep your building momentum',
  cards,
}: Props) => {
  const user = useStore(userStore.user);
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const updatePlanScreenData = useStore(pricingStore.updatePlanScreenData);

  useEffect(() => {
    if (!isOpen || !user) {
      return;
    }

    if (!user.activeOrganizationId) {
      proPricingPlansStore.initActiveState(user);
      teamPricingPlansStore.initInactiveState();

      return;
    }

    if (activeOrganization) {
      teamPricingPlansStore.initActiveState(activeOrganization);
    }
  }, [isOpen]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={cards.length > 1 ? 768 : 600}
        contentClassName="overflow-y-auto"
        maxHeight="90vh"
      >
        <HiddenDialogTitle>{title}</HiddenDialogTitle>
        <DialogContent>
          <div className="flex flex-col items-center gap-4 mt-4 mb-10">
            <h2 className="text-3xl font-semibold text-bolt-ds-textPrimary">{title}</h2>
            {message && <span className="text-bolt-ds-textSecondary text-center">{message}</span>}
          </div>

          <PricingIntervalSelector className="md:items-end lg:items-center flex-col items-center lg:flex-row-reverse grow-1 lg:basis-60 justify-start" />

          <div className={`grid grid-cols-1 gap-6 mt-6 sm:grid-cols-${cards.length}`}>
            {cards.includes('pro') && <ProPlansCard isPersonalWorkspace={true} tokenStatsMessage={null} />}
            {cards.includes('teams') && <TeamPlansCard tokenStatsMessage={null} />}
          </div>
        </DialogContent>
      </Dialog>
      <UpdatePlanScreenDialog open={!!updatePlanScreenData} />
    </>
  );
};
